import { useState, useMemo, useCallback } from 'react';
import { useSessionState } from '@/hooks/useSessionState';
import { SessionKey } from '@/types/storage';
import { useScreenState } from '@/hooks/useScreenState';
import { useAuth } from '@/hooks/useAuth';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import {
  useOrgConfigurationsQuery,
  useBoardTemplatesQuery,
} from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import { ScreenType } from '@/types/screen.types';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { ConfigurationConfigName } from '@/types/enums/ConfigurationConfigName';
import { UserRole } from '@/types/enums/UserRole';
import { logger } from '@/utils/logger';
import { useThemeColors } from '@/styles';
import { spacing } from '@/styles/design-tokens';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AsyncButton } from '@/components/ui/async-button';
import { IconName } from '@/icons';
import type {
  UpdateOrgConfigurationInput,
  UpdateOrgConfigurationOutput,
} from '@/types/generated';

// --- Config field schema ---

type FieldType = 'text' | 'number' | 'boolean';

interface ConfigFieldDef {
  key: string;
  label: string;
  type: FieldType;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
}

const CONFIG_FIELD_SCHEMAS: Record<ConfigurationConfigName, ConfigFieldDef[]> =
  {
    [ConfigurationConfigName.BOARD_TEMPLATE_DEFAULTS]: [], // Uses custom renderer
  };

const CONFIG_NAME_LABELS: Record<ConfigurationConfigName, string> = {
  [ConfigurationConfigName.BOARD_TEMPLATE_DEFAULTS]: 'Board Template Defaults',
};

// Build initial values from schema with all fields set to null
function buildInitialValues(fields: ConfigFieldDef[]): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const field of fields) {
    values[field.key] = null;
  }
  return values;
}

// Merge stored config with schema defaults (null for missing keys)
function mergeWithSchema(
  stored: Record<string, unknown> | undefined | null,
  fields: ConfigFieldDef[]
): Record<string, unknown> {
  const base = buildInitialValues(fields);
  if (!stored) return base;
  for (const field of fields) {
    if (field.key in stored && stored[field.key] !== undefined) {
      base[field.key] = stored[field.key];
    }
  }
  return base;
}

// Strip null values before saving (only send fields with actual values)
function stripNulls(values: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(values)) {
    if (val !== null && val !== undefined && val !== '') {
      result[key] = val;
    }
  }
  return result;
}

// --- Board Template Defaults types ---

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.STUDENT]: 'Student',
  [UserRole.MENTOR]: 'Mentor',
  [UserRole.ADMIN]: 'Admin',
};

const ROLES = [UserRole.STUDENT, UserRole.MENTOR, UserRole.ADMIN] as const;

interface RoleTemplateConfig {
  primary_template_id: string | null;
  secondary_template_id: string | null;
}

type BoardTemplateDefaultsConfig = Record<string, RoleTemplateConfig>;

function buildEmptyRoleConfig(): RoleTemplateConfig {
  return {
    primary_template_id: null,
    secondary_template_id: null,
  };
}

function buildEmptyDefaults(): BoardTemplateDefaultsConfig {
  const defaults: BoardTemplateDefaultsConfig = {};
  for (const role of ROLES) {
    defaults[role] = buildEmptyRoleConfig();
  }
  return defaults;
}

function mergeDefaultsConfig(
  stored: BoardTemplateDefaultsConfig | undefined | null
): BoardTemplateDefaultsConfig {
  const base = buildEmptyDefaults();
  if (!stored) return base;
  for (const role of ROLES) {
    if (stored[role]) {
      base[role] = { ...buildEmptyRoleConfig(), ...stored[role] };
    }
  }
  return base;
}

export default function ConfigManagement() {
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();
  const colors = useThemeColors();
  const toast = useToast();

  const [selectedConfigName, setSelectedConfigName] =
    useSessionState<ConfigurationConfigName>(
      SessionKey.LB_CONFIG_SELECTED_CONFIG_NAME,
      ConfigurationConfigName.BOARD_TEMPLATE_DEFAULTS
    );
  const [editValues, setEditValues] = useState<Record<string, unknown> | null>(
    null
  );
  const [isEditing, setIsEditing] = useState(false);

  const { data: configsData, isLoading } =
    useOrgConfigurationsQuery(activeOrganizationId);

  // Preload board templates so the dropdown is populated instantly when the
  // Board Template Defaults editor opens (shares cache with BoardTemplateDefaultsEditor).
  useBoardTemplatesQuery({ orgId: activeOrganizationId });

  const { execute, isLoading: isSaving } = useOrchestrator<
    UpdateOrgConfigurationInput,
    UpdateOrgConfigurationOutput
  >(Action.UPDATE_ORG_CONFIGURATION);

  const fields = useMemo(
    () => CONFIG_FIELD_SCHEMAS[selectedConfigName] ?? [],
    [selectedConfigName]
  );

  const selectedConfig = useMemo(() => {
    if (!configsData?.configurations) return null;
    return (
      configsData.configurations.find(
        c => c.config_name === selectedConfigName
      ) ?? null
    );
  }, [configsData, selectedConfigName]);

  // Current values merged with schema defaults
  const currentValues = useMemo(() => {
    const stored = selectedConfig?.config as
      | Record<string, unknown>
      | undefined;
    return mergeWithSchema(stored, fields);
  }, [selectedConfig, fields]);

  // Values to display (edit overrides or current)
  const displayValues = isEditing && editValues ? editValues : currentValues;

  const isCustomRenderer =
    selectedConfigName === ConfigurationConfigName.BOARD_TEMPLATE_DEFAULTS;

  const handleEdit = () => {
    if (isCustomRenderer) {
      const stored = selectedConfig?.config as
        | BoardTemplateDefaultsConfig
        | undefined;
      setEditValues(
        mergeDefaultsConfig(stored) as unknown as Record<string, unknown>
      );
    } else {
      setEditValues({ ...currentValues });
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValues(null);
  };

  const handleFieldChange = useCallback((key: string, value: unknown) => {
    setEditValues(prev => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const handleSave = async () => {
    if (!editValues) return;

    let config: Record<string, unknown>;
    if (isCustomRenderer) {
      // For board_template_defaults, strip roles with no templates selected
      const defaults = editValues as unknown as BoardTemplateDefaultsConfig;
      const cleaned: BoardTemplateDefaultsConfig = {};
      for (const role of ROLES) {
        const rc = defaults[role];
        if (rc && (rc.primary_template_id || rc.secondary_template_id)) {
          cleaned[role] = rc;
        }
      }
      config = cleaned as unknown as Record<string, unknown>;
    } else {
      config = stripNulls(editValues);
    }

    // organization_id is auto-injected by ActionOrchestrator before validation
    const result = await execute({
      config_name: selectedConfigName,
      config,
    } as UpdateOrgConfigurationInput);

    if (result.code === ResponseCode.SUCCESS) {
      toast.success('Configuration saved');
      setIsEditing(false);
      setEditValues(null);
    }
  };

  useScreenState({
    metadata: {
      screenType: ScreenType.CONFIG_MANAGEMENT,
      requiresAuth: true,
      showHeader: true,
      showSidebar: true,
    },
    onLoad: async (_strategy, state) => {
      logger.info('Config Management: Loading');
      state.setSuccess();
    },
    lifecycle: {
      onUnmount: () => {
        logger.info('Config Management unmounted');
      },
    },
  });

  if (!user) return null;

  return (
    <div
      className="flex flex-col h-full"
      style={{
        padding: spacing.lg,
        gap: spacing.lg,
        color: colors.text.primary,
      }}
    >
      <div className="flex flex-col gap-4 w-full">
        {/* Config name selector + action buttons inline */}
        <div className="flex items-center gap-3 justify-between">
          <Select
            value={selectedConfigName}
            onValueChange={v => {
              setSelectedConfigName(v as ConfigurationConfigName);
              setIsEditing(false);
              setEditValues(null);
            }}
          >
            <SelectTrigger className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(ConfigurationConfigName).map(name => (
                <SelectItem key={name} value={name}>
                  {CONFIG_NAME_LABELS[name] ?? name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <AsyncButton
                  onClick={handleSave}
                  isLoading={isSaving}
                  icon={IconName.SAVE}
                  loadingText="Saving..."
                >
                  Save
                </AsyncButton>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <AsyncButton
                onClick={async () => handleEdit()}
                icon={IconName.EDIT}
                disabled={isLoading}
              >
                Edit
              </AsyncButton>
            )}
          </div>
        </div>

        {/* Config fields */}
        {isLoading ? (
          <div className="text-sm" style={{ color: colors.text.secondary }}>
            Loading configurations...
          </div>
        ) : isCustomRenderer ? (
          <BoardTemplateDefaultsEditor
            config={
              isEditing
                ? (editValues as unknown as BoardTemplateDefaultsConfig)
                : mergeDefaultsConfig(
                    selectedConfig?.config as
                      | BoardTemplateDefaultsConfig
                      | undefined
                  )
            }
            disabled={!isEditing}
            onChange={updated =>
              setEditValues(updated as unknown as Record<string, unknown>)
            }
            colors={colors}
            organizationId={activeOrganizationId}
          />
        ) : (
          <div className="flex flex-col gap-2">
            {fields.map(field => (
              <ConfigField
                key={field.key}
                field={field}
                value={displayValues[field.key]}
                onChange={handleFieldChange}
                disabled={!isEditing}
                colors={colors}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Board Template Defaults Editor ---

interface BoardTemplateDefaultsEditorProps {
  config: BoardTemplateDefaultsConfig;
  disabled: boolean;
  onChange: (config: BoardTemplateDefaultsConfig) => void;
  colors: ReturnType<typeof useThemeColors>;
  organizationId: string | undefined;
}

function BoardTemplateDefaultsEditor({
  config,
  disabled,
  onChange,
  colors,
  organizationId,
}: BoardTemplateDefaultsEditorProps) {
  const { data: templatesData } = useBoardTemplatesQuery({
    orgId: organizationId,
  });

  const templates = templatesData?.templates ?? [];

  const [expandedRoles, setExpandedRoles] = useState<Record<string, boolean>>({
    student: true,
    mentor: false,
    admin: false,
  });

  const toggleRole = (role: string) => {
    setExpandedRoles(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const updateRoleConfig = (
    role: string,
    field: keyof RoleTemplateConfig,
    value: string | null
  ) => {
    const updated = { ...config };
    updated[role] = { ...updated[role], [field]: value };
    onChange(updated);
  };

  return (
    <div className="flex flex-col gap-3">
      {ROLES.map(role => {
        const rc = config[role] ?? buildEmptyRoleConfig();
        const isExpanded = expandedRoles[role] ?? false;
        const hasPrimary = !!rc.primary_template_id;
        const hasSecondary = !!rc.secondary_template_id;
        const summary = [
          hasPrimary ? 'Primary' : null,
          hasSecondary ? 'Secondary' : null,
        ]
          .filter(Boolean)
          .join(', ');

        return (
          <div
            key={role}
            className="rounded-lg border"
            style={{ borderColor: colors.border.default }}
          >
            <button
              type="button"
              className="flex items-center justify-between w-full px-4 py-3 text-left"
              onClick={() => toggleRole(role)}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  {ROLE_LABELS[role]}
                </span>
                {summary && (
                  <span
                    className="text-xs"
                    style={{ color: colors.text.secondary }}
                  >
                    ({summary})
                  </span>
                )}
              </div>
              <span
                className="text-xs"
                style={{ color: colors.text.secondary }}
              >
                {isExpanded ? '\u25B2' : '\u25BC'}
              </span>
            </button>

            {isExpanded && (
              <div
                className="px-4 pb-4 flex flex-col gap-4"
                style={{
                  borderTop: `1px solid ${colors.border.default}`,
                  paddingTop: spacing.md,
                }}
              >
                {/* Primary template */}
                <TemplateSlot
                  label="Primary Template"
                  templateId={rc.primary_template_id}
                  templates={templates}
                  disabled={disabled}
                  onTemplateChange={v =>
                    updateRoleConfig(role, 'primary_template_id', v)
                  }
                />

                {/* Secondary template */}
                <TemplateSlot
                  label="Secondary Template"
                  templateId={rc.secondary_template_id}
                  templates={templates}
                  disabled={disabled}
                  onTemplateChange={v =>
                    updateRoleConfig(role, 'secondary_template_id', v)
                  }
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// --- Template Slot ---

interface TemplateSlotProps {
  label: string;
  templateId: string | null;
  templates: Array<{ id: string; name: string }>;
  disabled: boolean;
  onTemplateChange: (templateId: string | null) => void;
}

const NONE_VALUE = '__none__';

function TemplateSlot({
  label,
  templateId,
  templates,
  disabled,
  onTemplateChange,
}: TemplateSlotProps) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">{label}</span>
      <Select
        value={templateId ?? NONE_VALUE}
        onValueChange={v => onTemplateChange(v === NONE_VALUE ? null : v)}
        disabled={disabled}
      >
        <SelectTrigger className="w-64">
          <SelectValue placeholder="No template" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>None</SelectItem>
          {templates.map(t => (
            <SelectItem key={t.id} value={t.id}>
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// --- Field renderer ---

interface ConfigFieldProps {
  field: ConfigFieldDef;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  disabled: boolean;
  colors: ReturnType<typeof useThemeColors>;
}

function FieldLabel({
  field,
  colors,
}: {
  field: ConfigFieldDef;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <div className="flex flex-col min-w-[180px] shrink-0">
      <span className="text-sm font-medium">{field.label}</span>
      {field.description && (
        <span className="text-xs" style={{ color: colors.text.secondary }}>
          {field.description}
        </span>
      )}
    </div>
  );
}

function ConfigField({
  field,
  value,
  onChange,
  disabled,
  colors,
}: ConfigFieldProps) {
  const displayValue = value === null || value === undefined ? '' : value;

  if (field.type === 'boolean') {
    const checked = value === true;
    return (
      <div className="flex items-center justify-between py-2 px-3 rounded-md">
        <FieldLabel field={field} colors={colors} />
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(field.key, e.target.checked)}
          disabled={disabled}
          className="h-4 w-4 rounded border"
          style={{ accentColor: colors.primary }}
        />
      </div>
    );
  }

  if (field.type === 'number') {
    const numValue = typeof value === 'number' ? value : null;
    const step = field.step ?? 1;
    const isOutOfRange =
      numValue !== null &&
      ((field.min !== undefined && numValue < field.min) ||
        (field.max !== undefined && numValue > field.max));

    const handleStep = (delta: number) => {
      if (disabled) return;
      const current = numValue ?? field.min ?? 0;
      const next = current + delta * step;
      if (field.min !== undefined && next < field.min) return;
      if (field.max !== undefined && next > field.max) return;
      onChange(field.key, next);
    };

    return (
      <div className="flex items-center justify-between py-2 px-3 rounded-md">
        <FieldLabel field={field} colors={colors} />
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleStep(-1)}
            disabled={
              disabled ||
              (numValue !== null &&
                field.min !== undefined &&
                numValue <= field.min)
            }
          >
            -
          </Button>
          <span
            className="flex items-center justify-center h-8 min-w-[48px] px-2 rounded-md border text-sm font-medium tabular-nums"
            style={{
              borderColor: isOutOfRange ? colors.error : colors.border.default,
              color: isOutOfRange ? colors.error : colors.text.primary,
            }}
          >
            {numValue !== null ? numValue : '-'}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleStep(1)}
            disabled={
              disabled ||
              (numValue !== null &&
                field.max !== undefined &&
                numValue >= field.max)
            }
          >
            +
          </Button>
          {isOutOfRange && (
            <span className="text-xs ml-1" style={{ color: colors.error }}>
              {field.min !== undefined && numValue! < field.min
                ? `Min ${field.min}`
                : `Max ${field.max}`}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 py-2 px-3 rounded-md">
      <FieldLabel field={field} colors={colors} />
      <Input
        type="text"
        value={String(displayValue)}
        placeholder={disabled ? 'Not set' : `Enter value`}
        onChange={e => {
          const raw = e.target.value;
          onChange(field.key, raw === '' ? null : raw);
        }}
        disabled={disabled}
        className="flex-1"
      />
    </div>
  );
}
